import { Component, Input } from '@angular/core';
import { Observable } from 'rxjs';

import { SyncStatus } from '../data/reducer';

/**
 * A sync status icon component that displays the current sync state of an entity.
 *
 * Usage:
 * ```html
 * <abs-sync-status-icon [status$]="syncStatus$"></abs-sync-status-icon>
 * ```
 *
 * Or with a static status:
 * ```html
 * <abs-sync-status-icon [status]="'Dirty'"></abs-sync-status-icon>
 * ```
 *
 * States:
 * - Clean: Hidden (no icon shown when synced)
 * - Dirty: Orange cloud-upload - local changes pending upload
 * - InProgress: Spinning sync icon - currently syncing
 * - Error: Red warning - sync failed
 *
 * Set [showClean]="true" to always show the green checkmark when synced.
 */
@Component({
    standalone: false,
    selector: 'abs-sync-status-icon',
    template: `
        <ng-container *ngIf="status$ | async as status; else staticStatus">
            <ng-container *ngTemplateOutlet="iconTemplate; context: { status: status }"></ng-container>
        </ng-container>

        <ng-template #staticStatus>
            <ng-container *ngTemplateOutlet="iconTemplate; context: { status: status }"></ng-container>
        </ng-template>

        <ng-template #iconTemplate let-status="status">
            <ng-container [ngSwitch]="status">
                <ion-icon
                    *ngSwitchCase="'Clean'"
                    [class.hidden]="!showClean"
                    name="cloud-done-outline"
                    class="sync-status sync-status--clean"
                    [style.color]="color"
                    title="Synced"
                ></ion-icon>
                <ion-icon
                    *ngSwitchCase="'Dirty'"
                    name="cloud-upload-outline"
                    class="sync-status sync-status--dirty"
                    [style.color]="color"
                    title="Unsaved changes"
                ></ion-icon>
                <ng-container *ngSwitchCase="'InProgress'">
                    <ion-icon
                        name="sync-outline"
                        class="sync-status sync-status--in-progress spinning"
                        [style.color]="color"
                        title="Syncing..."
                    ></ion-icon>
                </ng-container>
                <ion-icon
                    *ngSwitchCase="'Error'"
                    name="cloud-offline-outline"
                    class="sync-status sync-status--error"
                    [style.color]="color"
                    title="Sync failed"
                ></ion-icon>
            </ng-container>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .sync-status {
                font-size: 1.5em;
                transition:
                    opacity 0.2s ease,
                    color 0.2s ease;
            }

            .sync-status.hidden {
                display: none;
            }

            .sync-status--clean {
                color: var(--ion-color-success, #2dd36f);
            }

            .sync-status--dirty {
                color: var(--ion-color-warning-shade, #e0ac08);
            }

            .sync-status--in-progress {
                color: var(--ion-color-primary, #3880ff);
            }

            .sync-status--error {
                color: var(--ion-color-danger, #eb445a);
            }

            .spinning {
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }
        `
    ]
})
export class SyncStatusIconComponent {
    /**
     * Observable of the sync status. Use this for reactive updates.
     */
    @Input() status$?: Observable<SyncStatus>;

    /**
     * Static sync status. Use this when you have a non-reactive status.
     */
    @Input() status?: SyncStatus;

    /**
     * Whether to show the icon when status is Clean. Default is false (hidden when synced).
     */
    @Input() showClean = false;

    /**
     * Override color for all icons. When set, this color is applied to all status icons
     * instead of the default status-specific colors. Useful for buttons with colored backgrounds.
     */
    @Input() color?: string;
}
